import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { Box, Button, Card, Container, Fab, Grid, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import WebAssetOffIcon from '@mui/icons-material/WebAssetOff';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import PageTitle from "components/PageTitle";
import { useAuthContext } from "hooks/useAuth";

export default function NotePage() {

  const navigate = useNavigate();
  const { noteId } = useParams();
  const { user } = useAuthContext();

  const [isInvalidPermission, setIsInvalidPermission] = useState(false);
  const [isNoteNotFound, setIsNoteNotFound] = useState(false);
  const [noteGroupInfo, setNoteGroupInfo] = useState(null);
  const [noteItemList, setNoteItemList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/note/detail?noteId=${noteId}`)
    .then(res => {
      if (res.data) {
        if (res.data.noteGroupInfo.creditor === user.userId || res.data.noteGroupInfo.debtor === user.userId) {
          setNoteGroupInfo(res.data.noteGroupInfo);
          setNoteItemList(res.data.noteItemList);
        } else {
          setIsInvalidPermission(true);
        }
      } else {
        setIsNoteNotFound(true);
      }
    })
  }, []);

  useEffect(() => {
    if (!noteGroupInfo) return;
    setIsLoading(false);
  }, [noteGroupInfo]);

  useEffect(() => {
    if (!isNoteNotFound) return;
    setIsLoading(false);
  }, [isNoteNotFound]);

  useEffect(() => {
    if (!isInvalidPermission) return;
    setIsLoading(false);
  }, [isInvalidPermission]);

  return (
    <Container>
      {!isLoading ?
        !isNoteNotFound ?
          !isInvalidPermission ?
            <>
              <PageTitle>📒&nbsp;{noteGroupInfo.noteName}</PageTitle>
              <Grid container justifyContent={"space-between"} marginBottom="16px">
                <Typography fontFamily="NanumSquareBold" fontSize="1.25rem" lineHeight="1.25rem">
                  💵&nbsp;{noteGroupInfo.balance && noteGroupInfo.balance.toLocaleString()}
                </Typography>
                <Typography color="gray" fontSize="0.725rem">
                  {noteGroupInfo.creditorName && <>TO. {noteGroupInfo.creditorName}&nbsp;&nbsp;&nbsp;FROM. {noteGroupInfo.debtorName}</>}
                  </Typography>
              </Grid>

              {noteItemList ? 
                <Grid container flexDirection="column" gap="10px" marginBottom={"75px"}>
                  {noteItemList.length > 0 ?
                    noteItemList.map(item => {
                      return (
                        <NoteItem
                          amount={item.amount}
                          type={item.type}
                          balance={item.balance}
                          createdAt={item.createdAt}
                          createUserName={item.createUserName}
                          detail={item.detail}
                        />
                      )
                    })
                    :
                    <Typography textAlign="center" marginTop="16px" color="gray">등록된 항목이 없습니다.</Typography>
                  }
                </Grid>
                :
                null
              }

              <Fab
                color="primary" 
                aria-label="add" 
                sx={{position: "fixed", bottom: "10px", right: "10px"}}
                onClick={() => navigate(`/note/${noteId}/item/add`)}
              >
                <AddIcon/>
              </Fab>
            </>
            :
            <Grid container flexDirection="column" alignItems="center" sx={{color: "gray"}}>
              <LockIcon sx={{fontSize: "3rem", marginBottom: "4px"}}/>
              <Typography fontSize={"1.25rem"} marginBottom="32px">이 노트에 대한 접근 권한이 없습니다.</Typography>
              <Button variant="outlined" onClick={() => navigate("/")}>홈으로</Button>   
            </Grid>
          :
          <Grid container flexDirection="column" alignItems="center" sx={{color: "gray"}}>
            <WebAssetOffIcon sx={{fontSize: "3rem", marginBottom: "4px"}}/>
            <Typography fontSize={"1.25rem"} marginBottom="32px">존재하지 않는 노트입니다.</Typography>
            <Button variant="outlined" onClick={() => navigate("/")}>홈으로</Button>   
          </Grid>
      :
      null
    }
    </Container>
  );
}

function NoteItem({amount=0, type, balance, createdAt, createUserName, detail}) {
  const [isCollapse, setIsCollapse] = useState(false);
  
  return (
    <Card
      variant="outlined"
      sx={{boxSizing: "border-box"}} 
    >
      <Grid container justifyContent={"space-between"} alignItems={"flex-end"} sx={{padding: "0.625rem 0.875rem", borderBottom: "1px solid #dbdbdb"}}>
        <div>
          <Typography fontFamily="NanumSquareBold" fontSize="1.225rem" color={type === "I" ? "#1976d2" : "#DB4455"}>
            {type === "I" ? "+" : "-"}{amount.toLocaleString()}
          </Typography>

          <Typography fontSize="0.785rem" color="gray">
            잔액 : {balance.toLocaleString()}
          </Typography>
        </div>

        <Typography fontSize="0.785rem" color="gray">🙂&nbsp;{createUserName}&nbsp;&nbsp;&nbsp;📅&nbsp;{createdAt}</Typography>
      </Grid>
      <Box sx={{display:"grid", alignItems: "center", gridTemplateColumns: "1fr auto", padding: "0.625rem 0.875rem"}}>
        <Box
          fontSize="0.725rem"
          style={isCollapse ? null : {
            display: "-webkit-box",
            overflow: "hidden",
            textOverflow: "ellipsis",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical"
          }}
          sx={{
            color: "gray",
            width: "100%"
          }}
        >
          {detail}
        </Box>
        <Box display="flex" alignItems="flex-end" height="100%">
          {isCollapse ? 
            <KeyboardArrowUpIcon sx={{cursor: "pointer"}} onClick={() => setIsCollapse(!isCollapse)}/>
            :
            <KeyboardArrowDownIcon sx={{cursor: "pointer"}} onClick={() => setIsCollapse(!isCollapse)}
          />}
        </Box>
      </Box>
    </Card>
  );
}